import { Link } from "react-router-dom";
import ContactSection from "../../components/ContactSection";
import Icon from "../../components/icons";
import Reveal from "../../components/Reveal";
import SiteLayout from "../../components/SiteLayout";
import WebsiteLibrary from "../../features/WebsiteLibrary/WebsiteLibrary";
import { libraryBrand } from "../../features/WebsiteLibrary/Component";
import { websiteProjects } from "../../features/WebsiteLibrary/projects";

const promises = [
  ["01", "Designed around your brand", "Your visual identity, photography, offer, and customer journey shape the finished experience."],
  ["02", "A dashboard that belongs to you", "Change text, images, links, services, pricing, and key sections without touching code."],
  ["03", "Launch support included", "Responsive polish, forms, domain connection, launch checks, and a guided dashboard handoff."],
  ["04", "Ready to grow with you", "Add booking, payments, products, memberships, integrations, or entirely new workflows later."],
] as const;

const buildPaths = [
  {
    label: "Launch",
    title: "A focused site that looks established.",
    copy: "For a new business, event, professional, or campaign that needs a polished place to send people.",
    items: ["Focused 1–3 page experience", "Responsive custom design", "Personal editing dashboard", "Lead or contact form"],
  },
  {
    label: "Business",
    title: "Your complete digital front door.",
    copy: "For an active business that needs richer content, stronger conversion, and room to keep growing.",
    items: ["Custom multi-page website", "Advanced content dashboard", "Forms and enquiry management", "Analytics and launch support"],
    featured: true,
  },
  {
    label: "Commerce+",
    title: "A website that does the work.",
    copy: "For stores, booking flows, memberships, portals, and business-specific digital experiences.",
    items: ["Payments or booking", "Products or member content", "Custom workflows and integrations", "Ongoing growth options"],
  },
];

const process = [
  ["Discover", "Align the audience, goals, content, features, and the right build path."],
  ["Design", "Shape the visual direction and build the real responsive experience."],
  ["Refine", "Review the site, polish the details, and prepare everything for launch."],
  ["Own it", "Launch with dashboard access, training, and unlimited self-service editing."],
] as const;

const BrowserFrame = ({ image, label, className = "" }: { image: string; label: string; className?: string }) => (
  <div className={`tas-browser${className ? ` ${className}` : ""}`}>
    <div className="tas-browser-bar" aria-hidden="true">
      <span /><span /><span /><small>{label}</small>
    </div>
    <img src={image} alt={label} />
    <div className="tas-browser-glass" aria-hidden="true" />
  </div>
);

const TemplatesComponent = () => (
  <SiteLayout>
    <section className="tas-hero" aria-labelledby="tas-title">
      <div className="tas-hero-orb tas-hero-orb-one" aria-hidden="true" />
      <div className="tas-hero-orb tas-hero-orb-two" aria-hidden="true" />
      <div className="tas-hero-grid" aria-hidden="true" />
      <div className="site-container tas-hero-inner">
        <Reveal className="tas-hero-copy">
          <p className="section-kicker">CMJ Templates as a Service</p>
          <h1 id="tas-title">A website built for you. <em>A dashboard that stays yours.</em></h1>
          <p>Get a polished custom website without the drawn-out agency process—then keep it fresh with unlimited self-service editing from your private workspace.</p>
          <div className="tas-hero-actions">
            <a className="button" href="#contact">Build my website <Icon name="arrow" /></a>
            <a className="tas-quiet-link" href="#tas-work">Explore real work <span aria-hidden="true">↓</span></a>
          </div>
          <div className="tas-proof-pills" aria-label="Service highlights">
            <span><Icon name="check" /> Custom launch</span>
            <span><Icon name="check" /> Personal dashboard</span>
            <span><Icon name="check" /> Unlimited content edits</span>
          </div>
        </Reveal>

        <div className="tas-hero-stage" aria-label="Websites created by CMJ Software">
          <Reveal className="tas-stage-card tas-stage-left" delay={120} direction="left">
            <BrowserFrame image="/images/template-service/beauty-by-brittany-website.jpg" label="Beauty by Brittany" />
          </Reveal>
          <Reveal className="tas-stage-card tas-stage-main" delay={40} direction="scale">
            <BrowserFrame image="/images/template-service/glec-website.jpg" label="Great Lakes" />
          </Reveal>
          <Reveal className="tas-stage-card tas-stage-right" delay={180} direction="right">
            <BrowserFrame image="/images/template-service/eyala-website.jpg" label="Eyala" />
          </Reveal>
        </div>
      </div>
      <div className="tas-hero-foot" aria-hidden="true"><span>Strategy</span><i /> <span>Design</span><i /> <span>Engineering</span><i /> <span>Ownership</span></div>
    </section>

    <section className="tas-intro light-section">
      <div className="site-container">
        <Reveal className="tas-intro-heading">
          <p className="section-kicker">The service</p>
          <div>
            <h2>Launch faster. Look original. <em>Stay in control.</em></h2>
            <p>Templates as a Service combines proven CMJ design systems with the attention of a custom build. The foundation makes delivery more efficient; the final experience still feels unmistakably yours.</p>
          </div>
        </Reveal>
        <div className="tas-promise-grid">
          {promises.map(([number, title, copy], index) => (
            <Reveal key={number} delay={index * 65}>
              <article><span>{number}</span><h3>{title}</h3><p>{copy}</p></article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section id="tas-work" aria-label="Explore websites built by CMJ Software">
      <WebsiteLibrary {...libraryBrand} projects={websiteProjects} compact />
    </section>

    <section className="tas-dashboard light-section" aria-labelledby="tas-dashboard-title">
      <div className="site-container tas-dashboard-grid">
        <Reveal className="tas-dashboard-copy" direction="left">
          <p className="section-kicker">Your private workspace</p>
          <h2 id="tas-dashboard-title">The website is only <em>half the product.</em></h2>
          <p>Your dashboard turns everyday updates into a simple business task. Change what customers see, review enquiries, preview the result, and publish when you are ready.</p>
          <ol>
            <li><span>01</span><div><strong>Edit your content</strong><small>Keep services, images, pricing, and details current.</small></div></li>
            <li><span>02</span><div><strong>See incoming enquiries</strong><small>Keep new customer conversations within reach.</small></div></li>
            <li><span>03</span><div><strong>Preview before publishing</strong><small>Make changes confidently without touching code.</small></div></li>
          </ol>
        </Reveal>
        <Reveal className="tas-dashboard-visual" direction="right" delay={120}>
          <div className="tas-dashboard-orbit" aria-hidden="true" />
          <BrowserFrame image="/images/template-service/glec-dashboard.jpg" label="Great Lakes owner dashboard" className="tas-dashboard-frame" />
          <div className="tas-editing-badge"><strong>∞</strong><span>Unlimited editing<small>No per-edit fees</small></span></div>
        </Reveal>
      </div>
    </section>

    <section className="tas-process dark-section" aria-labelledby="tas-process-title">
      <div className="site-container">
        <Reveal className="tas-section-heading tas-section-heading-dark">
          <div><p className="section-kicker">A clearer process</p><h2 id="tas-process-title">From idea to live website. <em>Without the usual drag.</em></h2></div>
          <p>A focused four-part process keeps decisions clear, progress visible, and your launch moving.</p>
        </Reveal>
        <div className="tas-process-grid">
          {process.map(([title, copy], index) => (
            <Reveal key={title} delay={index * 65}><article><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article></Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="tas-plans light-section" aria-labelledby="tas-plans-title">
      <div className="site-container">
        <Reveal className="tas-section-heading">
          <div><p className="section-kicker">Choose your build path</p><h2 id="tas-plans-title">The right amount of website. <em>Room to grow later.</em></h2></div>
          <p>Every project receives a clear written scope and fixed quote. Choose the closest fit and we will shape the details together.</p>
        </Reveal>
        <div className="tas-plan-grid">
          {buildPaths.map((path, index) => (
            <Reveal key={path.label} delay={index * 70}>
              <article className={path.featured ? "is-featured" : ""}>
                {path.featured && <span className="tas-plan-badge">Most popular</span>}
                <p className="tas-plan-label">{path.label}</p>
                <h3>{path.title}</h3>
                <p>{path.copy}</p>
                <ul>{path.items.map((item) => <li key={item}><Icon name="check" />{item}</li>)}</ul>
                <a className="button button-secondary" href="#contact">Choose {path.label} <Icon name="arrow" /></a>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal className="tas-payment-bar">
          <div className="tas-payment-copy"><span><Icon name="check" /></span><div><strong>Already have an approved scope?</strong><small>Pay securely for an authorized web development hour. Work only begins after written approval.</small></div></div>
          <div className="tas-payment-price"><strong>$150</strong><span>/ approved hour</span></div>
          <a href="https://checkout.cmjsoftware.com/b/bJeeVc99r1DZdbafJ4eAg0C" target="_blank" rel="noreferrer">Pay securely <Icon name="arrow" /></a>
        </Reveal>
      </div>
    </section>

    <section className="tas-closing dark-section">
      <div className="site-container tas-closing-grid">
        <Reveal><p className="section-kicker">Built to be yours</p><h2>Not a rented theme. <em>A real digital home.</em></h2></Reveal>
        <Reveal delay={100}><p>Own the experience, keep it current, and grow it when the business is ready. CMJ Software stays available for the work that should still feel custom.</p><Link className="text-link" to="/about">Meet CMJ Software <Icon name="arrow" /></Link></Reveal>
      </div>
    </section>

    <ContactSection prefill={{ projectType: "Website or web platform", message: "I'm interested in Templates as a Service and would like help choosing the right build path." }} />
  </SiteLayout>
);

export default TemplatesComponent;
